import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEqual, isFunction } from "lodash";

import { getDisplayName } from "../Utils/getDisplayName";

import { initializeList } from "../../reducers/resourcesList/initialize";
import { incrementResourcesListVersion } from "../../reducers/resourcesList/version";
import { highlight } from "../../helpers/StringHelper";

const LIST_KEY_PREFIX = "resourcesList";

export const RESOURCES_LIST_CONTRIBUTION_TYPE = "contribution";
export const RESOURCES_LIST_USER_TYPE = "user";

export const ResourcesListContext = createContext();

// This wrapper component acts as a provider for the `resourcesList` feature which allows
// child components to search and filter data from the resources list and adapt their
// rendering to the current redux-backed list state.
//
export const ResourcesListProvider = props => {
  const dispatch = useDispatch();
  const resourcesListKeyRef = useRef(createResourcesListKey());
  const resourcesListKey = resourcesListKeyRef.current;

  const resourcesList = useSelector(
    ({ resourcesList }) => resourcesList[resourcesListKey]
  );
  const { children } = props;

  // Initialize the resourcesList in the reduxStore when the
  useEffect(() => {
    dispatch(initializeList(resourcesListKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wait for the resourcesList to be initialized in the store before rendering the child
  // components tree.
  if (!resourcesList) return null;

  return (
    <ResourcesListContext.Provider value={{ resourcesListKey }}>
      {/* This allows passing a function as the <ResourcesListProvider/> component to get
          the resourcesList and resourcesListKey available for its children */}
      {isFunction(children) ? children({ resourcesList, resourcesListKey }) : children}
    </ResourcesListContext.Provider>
  );
};

const createResourcesListKey = () => {
  const time = new Date().getTime();
  const random = Math.random(100000000);

  return `${LIST_KEY_PREFIX}:${time}:${random}`;
};

export default function resourcesList(WrappedComponent) {
  const ResourcesList = props => (
    <ResourcesListProvider>
      {resourcesListKey => (
        <WrappedComponent {...props} resourcesListKey={resourcesListKey} />
      )}
    </ResourcesListProvider>
  );

  ResourcesList.displayName = `ResourcesList(${getDisplayName(WrappedComponent)})`;

  return ResourcesList;
}

// This allows fetching the current resourcesList data from the redux store, using the
// stored resourcesListKey in the current component's context.
//
// If the resourcesListKey is not available in the context, the hook will return an empty
// object, which allows for components to safely use the hook without having to check for
// the resourcesListKey existence, when they're reused in other contexts.
//
export const useResourcesList = () => {
  const { resourcesListKey } = useContext(ResourcesListContext) || {};

  const resourcesList = useSelector(
    ({ resourcesList }) => resourcesListKey && resourcesList[resourcesListKey]
  );

  return { resourcesListKey, ...resourcesList };
};

const useResourcesListFiltersValue = () => {
  const { search } = useResourcesList();
  const { terms, selectedThemes, bookmarkedOnly, mineOnly } = search;
  const selectedThemeIds = selectedThemes.map(({ id }) => id);

  return {
    search: terms,
    themeIds: selectedThemeIds,
    bookmarks: bookmarkedOnly,
    mine: mineOnly
  };
};

// This hook allows for getting the current resources list filter, and to subscribe to
// any update of its values.
//
export const useResourcesListFilters = onFilteringChanged => {
  const { search, themeIds, bookmarks, mine } = useResourcesListFiltersValue();

  const searchRef = useRef(search);
  const bookmarksRef = useRef(bookmarks);
  const mineRef = useRef(mine);
  const themeIdsRef = useRef(themeIds);

  // This is used to track filters changes between renders and `useEffect` calls. React
  // doesn't seem to call `useEffect` during every render when called from our
  // useResourcesListFiltering, which may result in refs having already being updated
  // before the render that will call `useEffect` is run, and prevent the callback to be
  // called.
  //
  // So we use two refs to keep track of the current filters change version, and the
  // commited version, which is the one for which the callback was called the last time.
  //
  const filtersVersion = useRef(0);
  const committedFiltersVersion = useRef(0);

  let filtersDidChange = false;

  if (search !== searchRef.current) {
    searchRef.current = search;
    filtersDidChange = true;
  }

  // Here we use lodash's deep comparison to check the equality
  if (!isEqual(themeIds, themeIdsRef.current)) {
    themeIdsRef.current = themeIds;
    filtersDidChange = true;
  }

  if (bookmarks !== bookmarksRef.current) {
    bookmarksRef.current = bookmarks;
    filtersDidChange = true;
  }

  if (mine !== mineRef.current) {
    mineRef.current = mine;
    filtersDidChange = true;
  }

  const filters = {
    search: searchRef.current,
    themeIds: themeIdsRef.current,
    bookmarks: bookmarksRef.current,
    mine: mineRef.current
  };

  if (filtersDidChange) filtersVersion.current++;

  useEffect(() => {
    if (
      filtersVersion.current !== committedFiltersVersion.current &&
      onFilteringChanged
    ) {
      committedFiltersVersion.current = filtersVersion.current;
      onFilteringChanged(filters, { filtersChanged: true });
    }
  });

  return filters;
};

// This hooks allows to get the current resources list sorting order, and to subscribe to
// any update of its values.
//
export const useResourcesListSorting = onSortingChanged => {
  const { sortOrder } = useResourcesList();
  const { field, direction, seed } = sortOrder;

  const fieldRef = useRef(field);
  const directionRef = useRef(direction);
  const seedRef = useRef(seed);

  let sortingDidChange = false;

  if (field !== fieldRef.current) {
    fieldRef.current = field;
    sortingDidChange = true;
  }

  if (direction !== directionRef.current) {
    directionRef.current = direction;
    sortingDidChange = true;
  }

  if (seed !== seedRef.current) {
    seedRef.current = seed;
    sortingDidChange = true;
  }

  useEffect(() => {
    if (sortingDidChange && onSortingChanged) {
      onSortingChanged(sortOrder, { sortingChanged: true });
    }
  });

  return sortOrder;
};

// This hook allows components to subscribe to the resources list filtering and sorting
// events with a single callback.
//
// This hook is used to refetch the resources from the backend server whenever one of
// its filtering or sorting param is updated.
//
// Note : If the hook is used to refetch data from the server, like the
//        ContributionsListFiltersLoader component does, it should only be used in one
//        component from a given resourcesList tree at the same time, to avoid the server
//        to be queried multiple times for the same request params.
//
export const useResourcesListFiltering = onResourcesListParamsUpdated => {
  const { resourcesListKey, sortOrder } = useResourcesList();

  let filters = useResourcesListFiltersValue();
  let sort = sortOrder;

  const dispatch = useDispatch();
  const onComplete = useCallback(
    // We use a setTimeout here to avoid issues with React state update during rendering
    // as warned in React 18.
    () => setTimeout(() => dispatch(incrementResourcesListVersion(resourcesListKey)), 0),
    [dispatch, resourcesListKey]
  );

  const handleFilteringChanged = useCallback(
    filters => onResourcesListParamsUpdated({ filters, sort, onComplete }),
    [sort, onComplete, onResourcesListParamsUpdated]
  );

  const handleSortingChanged = useCallback(
    sort => onResourcesListParamsUpdated({ filters, sort, onComplete }),
    [filters, onComplete, onResourcesListParamsUpdated]
  );

  filters = useResourcesListFilters(handleFilteringChanged);
  sort = useResourcesListSorting(handleSortingChanged);
};

// This hook allows components to subscribe to the following events :
//
// - beforeRefetch : When the resources list was filtered or sorted and a request is going
//                   to be sent to the backend server. This allows for example to display
//                   loading state feedback to the user.
//
// - onRefetched   : When the resources list was refetched and is ready to be reloaded
//                   in the component. This allows for example to locally refetch the
//                   resources from the relay local store.
//
export const useResourcesListFilteringRefetched = ({ beforeRefetch, onRefetched }) => {
  const { version } = useResourcesList();
  const filters = useResourcesListFilters(beforeRefetch);
  const sort = useResourcesListSorting(beforeRefetch);

  useEffect(() => {
    if (version) onRefetched({ filters, sort });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);
};

// This hook allows to highlight a string with the search terms from the resources list.
//
// If no search terms are available, or the resourcesList doesn't exist, it silently
// doesn't highlight anything.
//
export const useHighlightSearch = () => {
  const { search } = useResourcesList();
  const searchTerms = search?.terms;

  return useCallback(string => highlight(string, searchTerms), [searchTerms]);
};
